import React, { useEffect, useState } from 'react';
import axios from '../../utils/axiosConfig';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer
} from 'recharts';

interface UsersTrendPoint {
  date: string;
  newUsers: number;
  activeUsers: number;
}

interface EngagementTrendPoint {
  date: string;
  quizzesCompleted: number;
  averageScore: number;
  averageCompletionTime: number;
}

interface CombinedPoint {
  date: string;
  activeUsers: number;
  quizzesCompleted: number;
}

interface UsersEngagementLineChartProps {
  className?: string;
  days?: 7 | 30 | 90;
  includeBots?: boolean;
}

type DaysRange = 7 | 30 | 90;

const formatDateLabel = (raw: string): string => {
  const date = new Date(raw);
  if (Number.isNaN(date.getTime())) {
    return raw;
  }
  return date.toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric'
  });
};

const UsersEngagementLineChart: React.FC<UsersEngagementLineChartProps> = ({
  className,
  days: daysProp = 30,
  includeBots = false
}) => {
  const [days, setDays] = useState<DaysRange>(daysProp as DaysRange);
  
  // Update local state when prop changes
  useEffect(() => {
    setDays(daysProp as DaysRange);
  }, [daysProp]);
  const [data, setData] = useState<CombinedPoint[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const load = async () => {
      try {
        setLoading(true);
        setError(null);

        const [usersRes, engagementRes] = await Promise.all([
          axios.get<{ success: boolean; data: UsersTrendPoint[] }>(
            '/api/admin/metrics/users/trends',
            { params: { days, includeBots } }
          ),
          axios.get<{ success: boolean; data: EngagementTrendPoint[] }>(
            '/api/admin/metrics/engagement/trends',
            { params: { days, includeBots } }
          )
        ]);

        if (!usersRes.data.success || !engagementRes.data.success) {
          throw new Error('Failed to load trend metrics');
        }

        const usersByDate = new Map<string, UsersTrendPoint>();
        for (const point of usersRes.data.data) {
          usersByDate.set(point.date, point);
        }

        const engagementByDate = new Map<string, EngagementTrendPoint>();
        for (const point of engagementRes.data.data) {
          engagementByDate.set(point.date, point);
        }

        const allDates = Array.from(
          new Set<string>([
            ...Array.from(usersByDate.keys()),
            ...Array.from(engagementByDate.keys())
          ])
        ).sort();

        const combined: CombinedPoint[] = allDates.map((date) => {
          const u = usersByDate.get(date);
          const e = engagementByDate.get(date);
          return {
            date,
            activeUsers: u?.activeUsers ?? 0,
            quizzesCompleted: e?.quizzesCompleted ?? 0
          };
        });

        if (!cancelled) {
          setData(combined);
        }
      } catch (err: any) {
        console.error('[UsersEngagementLineChart] Failed to load trends', err);
        if (!cancelled) {
          setError('Failed to load user and quiz trends.');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    load();

    return () => {
      cancelled = true;
    };
  }, [days, includeBots]);

  if (loading && data.length === 0) {
    return (
      <div className={className}>
        <div className="admin-dashboard-card__meta">Loading trend data...</div>
      </div>
    );
  }

  if (error && data.length === 0) {
    return (
      <div className={className}>
        <div className="admin-dashboard-card__meta">{error}</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className={className}>
        <div className="admin-dashboard-card__meta">
          No trend data available yet. Once users start completing quizzes,
          their activity will appear here.
        </div>
      </div>
    );
  }

  return (
    <div className={className}>
      <div style={{ width: '100%', height: 260 }}>
        <ResponsiveContainer width="100%" height="100%">
          <LineChart
            data={data}
            margin={{ top: 10, right: 20, left: 0, bottom: 20 }}
          >
            <CartesianGrid
              strokeDasharray="3 3"
              stroke="var(--surface-subtle, #e5e7eb)"
            />
            <XAxis
              dataKey="date"
              tickFormatter={formatDateLabel}
              stroke="var(--text-muted, #6b7280)"
              tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 11 }}
              height={40}
            />
            <YAxis
              stroke="var(--text-muted, #6b7280)"
              tick={{ fill: 'var(--text-muted, #6b7280)', fontSize: 11 }}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--surface-elevated, #ffffff)',
                border: '1px solid var(--surface-subtle, #e5e7eb)',
                borderRadius: 4,
                fontSize: 12
              }}
              labelFormatter={(label: string) => formatDateLabel(label)}
            />
            <Legend
              wrapperStyle={{ paddingTop: 8 }}
              formatter={(value: string) =>
                value === 'activeUsers' ? 'Active users' : 'Quizzes completed'
              }
            />
            <Line
              type="monotone"
              dataKey="activeUsers"
              stroke="#6366f1"
              strokeWidth={2}
              dot={false}
              name="activeUsers"
            />
            <Line
              type="monotone"
              dataKey="quizzesCompleted"
              stroke="#10b981"
              strokeWidth={2}
              dot={false}
              name="quizzesCompleted"
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
};

export default UsersEngagementLineChart;



